import { useState, useEffect, useCallback, useRef } from 'react';
import { useNostrPublish, getReadRelays, getCurrentPubkey } from './useNostrPublish';

const BOOKMARK_KIND = 10003;

// In-memory cache of current user's bookmarks
let cachedBookmarks: string[] | null = null;
let cachedPubkey: string | null = null;
let fetchPromise: Promise<string[]> | null = null;

// Listeners for bookmark changes
const listeners = new Set<() => void>();
function notifyListeners() {
  listeners.forEach(fn => fn());
}

/**
 * Fetch bookmarks from relays for a given pubkey.
 * Returns an array of event IDs that are bookmarked.
 */
async function fetchBookmarksFromRelays(pubkey: string): Promise<string[]> {
  const relays = getReadRelays(); // all relays, not just 4
  let bestEvent: any = null;

  await Promise.allSettled(
    relays.map(url => new Promise<void>(resolve => {
      try {
        const ws = new WebSocket(url);
        const subId = 'bkmk_' + Math.random().toString(36).slice(2, 8);
        ws.onopen = () => {
          ws.send(JSON.stringify(['REQ', subId, {
            kinds: [BOOKMARK_KIND],
            authors: [pubkey],
            limit: 10, // fetch several in case replaceable event is duplicated
          }]));
        };
        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT' && data[1] === subId) {
              const ev = data[2];
              if (!bestEvent || ev.created_at > bestEvent.created_at) {
                bestEvent = ev;
              }
            }
            if (data[0] === 'EOSE' && data[1] === subId) {
              try { ws.close(); } catch {}
              resolve();
            }
          } catch {}
        };
        ws.onerror = () => resolve();
        setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 10000); // 10s timeout
      } catch { resolve(); }
    }))
  );

  if (!bestEvent) return [];

  // Extract bookmarked event IDs from 'e' tags
  const eventIds = bestEvent.tags
    .filter((t: string[]) => t[0] === 'e')
    .map((t: string[]) => t[1]);

  return eventIds;
}

/**
 * Hook to manage bookmarks for the current user.
 *
 * IMPORTANT: The background refetch never clears the cache. It only
 * updates if the relay returns a newer/larger result. This prevents
 * the "appears then disappears" flash where the UI briefly shows
 * empty bookmarks while re-fetching.
 */
export function useBookmarks() {
  const { publish } = useNostrPublish();
  const [bookmarks, setBookmarks] = useState<string[]>(cachedBookmarks || []);
  const [loading, setLoading] = useState(!cachedBookmarks);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Subscribe to changes from other hook instances / optimistic updates
  useEffect(() => {
    const update = () => {
      if (mountedRef.current && cachedBookmarks) {
        setBookmarks([...cachedBookmarks]);
      }
    };
    listeners.add(update);
    return () => { listeners.delete(update); };
  }, []);

  // Background-safe refetch: fetches fresh data from relays but NEVER
  // clears the existing cache while loading. Only replaces the cache
  // once new data arrives (and only if it's actually newer).
  const backgroundRefetch = useCallback(async (pubkey: string) => {
    try {
      const ids = await fetchBookmarksFromRelays(pubkey);
      // Only update if we got results — don't clear to empty on a failed/partial fetch.
      // Also keep the larger set: if the user just added a bookmark optimistically,
      // the relay may not have it yet, so the optimistic cache could be bigger.
      if (ids.length > 0 || cachedBookmarks === null) {
        // Merge: keep any optimistically-added IDs that the relay hasn't caught up to yet
        const merged = cachedBookmarks
          ? [...new Set([...ids, ...cachedBookmarks.filter(id => !ids.includes(id) && cachedBookmarks!.indexOf(id) > (cachedBookmarks!.length - 5))])]
          : ids;
        // But prefer the relay result as the canonical set — only keep very recent
        // optimistic additions (last ~5) that might not have propagated yet
        cachedBookmarks = merged.length > 0 ? merged : ids;
        notifyListeners();
      }
    } catch {}
  }, []);

  const forceRefetch = useCallback(async () => {
    try {
      // Read the pubkey from localStorage, NOT the signer — see getCurrentPubkey.
      const pubkey = getCurrentPubkey();
      if (!pubkey) return;
      if (mountedRef.current) setLoading(true);
      cachedPubkey = pubkey;
      await backgroundRefetch(pubkey);
      if (mountedRef.current && cachedBookmarks) {
        setBookmarks([...cachedBookmarks]);
      }
    } catch {} finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [backgroundRefetch]);

  // Fetch bookmarks on mount
  useEffect(() => {
    const loadBookmarks = async () => {
      try {
        // Read the pubkey from localStorage, NOT the signer. This effect runs
        // once per PostCard (one per feed item), so calling getPublicKey() here
        // used to fire a storm of signer calls that locked up extensions and
        // remote signers — breaking login and content loading app-wide.
        const pubkey = getCurrentPubkey();
        if (!pubkey) { setLoading(false); return; }

        // If we have cached data for this user, show it immediately
        // then refresh in the background WITHOUT clearing the cache
        if (cachedPubkey === pubkey && cachedBookmarks !== null) {
          setBookmarks([...cachedBookmarks]);
          setLoading(false);
          // Background refresh — does NOT null out the cache
          backgroundRefetch(pubkey);
          return;
        }

        // Avoid duplicate initial fetches
        if (fetchPromise && cachedPubkey === pubkey) {
          const ids = await fetchPromise;
          cachedBookmarks = ids;
          if (mountedRef.current) setBookmarks([...ids]);
          setLoading(false);
          return;
        }

        cachedPubkey = pubkey;
        fetchPromise = fetchBookmarksFromRelays(pubkey);

        const ids = await fetchPromise;
        cachedBookmarks = ids;
        fetchPromise = null;
        notifyListeners();
        if (mountedRef.current) {
          setBookmarks([...ids]);
        }
      } catch {} finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    loadBookmarks();
  }, [backgroundRefetch]);

  const isBookmarked = useCallback((eventId: string): boolean => {
    return (cachedBookmarks || []).includes(eventId);
  }, [bookmarks]);

  const addBookmark = useCallback(async (eventId: string) => {
    // If cache is empty/null, fetch fresh from relays first to avoid overwriting
    if (!cachedBookmarks || cachedBookmarks.length === 0) {
      try {
        const pubkey = getCurrentPubkey();
        if (pubkey) {
          const fresh = await fetchBookmarksFromRelays(pubkey);
          cachedBookmarks = fresh;
          cachedPubkey = pubkey;
        }
      } catch {}
    }
    if (!cachedBookmarks) cachedBookmarks = [];
    if (cachedBookmarks.includes(eventId)) return;

    // Optimistically update
    cachedBookmarks = [...cachedBookmarks, eventId];
    notifyListeners();

    // Publish updated bookmark list
    const tags = cachedBookmarks.map(id => ['e', id]);
    await publish({
      kind: BOOKMARK_KIND,
      content: '',
      tags,
      created_at: Math.floor(Date.now() / 1000),
    });
  }, [publish, bookmarks]);

  const removeBookmark = useCallback(async (eventId: string) => {
    if (!cachedBookmarks) return;
    if (!cachedBookmarks.includes(eventId)) return;

    // Optimistically update
    cachedBookmarks = cachedBookmarks.filter(id => id !== eventId);
    notifyListeners();

    // Publish updated bookmark list
    const tags = cachedBookmarks.map(id => ['e', id]);
    await publish({
      kind: BOOKMARK_KIND,
      content: '',
      tags,
      created_at: Math.floor(Date.now() / 1000),
    });
  }, [publish, bookmarks]);

  const toggleBookmark = useCallback(async (eventId: string) => {
    if (isBookmarked(eventId)) {
      await removeBookmark(eventId);
    } else {
      await addBookmark(eventId);
    }
  }, [isBookmarked, addBookmark, removeBookmark]);

  return {
    bookmarks,
    loading,
    isBookmarked,
    addBookmark,
    removeBookmark,
    toggleBookmark,
    refetch: forceRefetch,
  };
}

